import type Employee from '@/model/Employee'
import type { CompanyEmployee } from '@/model/Employee'
import type Company from '@/model/Company'
import type FeatureFlagsCompany from '@/model/FeatureFlagsCompany'
import type { FeatureFlagsGlobal } from '@/model/FeatureFlagsGlobal'
import { apiService } from '@/services/ApiService'
import { companyService } from '@/services/CompanyService'
import { employeeService } from '@/services/EmployeeService'
import { featureFlagsService } from '@/services/FeatureFlagsService'
import { ServiceError } from '@/services/util/ServiceError'
import { isCompanyAccessBlocked } from '@/utils/companyBlock'
import { resolveStripeBillingEnabled } from '@/utils/stripeBillingFlagUtils'
import { resolveTransportDocumentV2SchemaEnabled } from '@/utils/transportDocumentV2SchemaFlagUtils'
import { defineStore } from 'pinia'

const CURRENT_ACCOUNT_ID_KEY = 'currentAccountId'
const COMPANY_FEATURE_FLAGS_KEY_PREFIX = 'companyFeatureFlags:'
const GLOBAL_FEATURE_FLAGS_KEY = 'globalFeatureFlags'

let profileLoadPromise: Promise<Employee | ServiceError> | null = null

export const useProfileStore = defineStore('profile-store', {
  state: () => ({
    // authUserToken: '' as string,
    profile: undefined as Employee | undefined,
    selectedCompany: {} as CompanyEmployee,
    selectedCompanyRecord: undefined as Company | undefined,
    availableCompanies: [] as CompanyEmployee[],
    companyFeatureFlags: {} as Record<string, FeatureFlagsCompany | null>,
    globalFeatureFlags: null as FeatureFlagsGlobal | null
  }),
  getters: {
    getProfile: (applicationState: any) => applicationState.profile,
    getSelectedCompany: (applicationState: any) => applicationState.selectedCompany,
    getAvailableCompanies: (applicationState: any) => applicationState.availableCompanies,
    getCompanyFeatureFlags: (applicationState: any) => (companyId: string) => applicationState.companyFeatureFlags[companyId],
    getGlobalFeatureFlags: (applicationState: any) => applicationState.globalFeatureFlags,
    isStripeBillingEnabled: (applicationState: any) => (companyId: string) =>
      resolveStripeBillingEnabled(
        applicationState.companyFeatureFlags[companyId],
        applicationState.globalFeatureFlags
      ),
    isTransportDocumentV2SchemaEnabled: (applicationState: any) => (companyId: string) =>
      resolveTransportDocumentV2SchemaEnabled(
        applicationState.companyFeatureFlags[companyId],
        applicationState.globalFeatureFlags
      ),
    isProfileLoaded: (applicationState: any) => applicationState.profile !== undefined,
    isSelectedCompanyAccessBlocked: (applicationState: any) =>
      isCompanyAccessBlocked(applicationState.selectedCompanyRecord?.block)
  },
  actions: {
    async ensureProfileLoaded(force = false): Promise<Employee | ServiceError> {
      if (this.profile && !force) {
        return this.profile
      }

      if (profileLoadPromise) {
        return profileLoadPromise
      }

      profileLoadPromise = employeeService.findMe(force).finally(() => {
        profileLoadPromise = null
      })

      return profileLoadPromise
    },

    async saveProfile(employee: Employee) {
      if (!employee) {
        return
      }

      this.profile = employee

      this.availableCompanies = employee.companies

      const companyId = localStorage.getItem(CURRENT_ACCOUNT_ID_KEY)

      if (!companyId) {
        this.setSelectedCompany(employee?.companies[0])
      } else {
        const companyFound = employee?.companies?.find(
          (company: CompanyEmployee) => company.id === companyId
        )

        if (companyFound?.id) {
          this.setSelectedCompany(companyFound)
        } else {
          this.setSelectedCompany(employee?.companies[0])
        }
      }
    },
    async setSelectedCompany(company: CompanyEmployee) {
      localStorage.setItem(CURRENT_ACCOUNT_ID_KEY, company.id)

      this.selectedCompany = company
      await Promise.all([
        this.loadCompanyFeatureFlags(company.id),
        this.loadGlobalFeatureFlags(),
        this.loadSelectedCompanyRecord(company.id)
      ])
    },

    async loadGlobalFeatureFlags(): Promise<FeatureFlagsGlobal | undefined> {
      const response = await apiService.getFeatureFlagsGlobal()
      if (response instanceof ServiceError) {
        return undefined
      }

      this.globalFeatureFlags = response
      localStorage.setItem(GLOBAL_FEATURE_FLAGS_KEY, JSON.stringify(response))
      return response
    },

    async loadSelectedCompanyRecord(companyId: string): Promise<void> {
      const response = await companyService.findCompany(companyId)
      if (response instanceof ServiceError) {
        this.selectedCompanyRecord = undefined
        return
      }
      this.selectedCompanyRecord = response
    },

    async ensureSelectedCompanyRecordLoaded(): Promise<void> {
      const companyId = this.selectedCompany?.id
      if (!companyId) {
        this.selectedCompanyRecord = undefined
        return
      }
      if (this.selectedCompanyRecord?.id === companyId) {
        return
      }
      await this.loadSelectedCompanyRecord(companyId)
    },

    async loadCompanyFeatureFlags(companyId: string): Promise<FeatureFlagsCompany | undefined> {
      const storageKey = `${COMPANY_FEATURE_FLAGS_KEY_PREFIX}${companyId}`


      const response = await featureFlagsService.getFeatureFlags(companyId)
      if (response instanceof ServiceError) {
        return undefined
      }

      this.companyFeatureFlags[companyId] = response
      localStorage.setItem(storageKey, JSON.stringify(response))
      return response
    },

    async refreshProfileIfCurrentUser(employeeId: string): Promise<void> {
      if (this.profile?.id !== employeeId) {
        return
      }

      const response = await this.ensureProfileLoaded(true)
      if (!(response instanceof ServiceError)) {
        await this.saveProfile(response)
      }
    },

    deleteProfile() {
      profileLoadPromise = null
      this.profile = undefined
      this.selectedCompany = undefined as any
      this.selectedCompanyRecord = undefined
      this.availableCompanies = [] as CompanyEmployee[]
      localStorage.removeItem(CURRENT_ACCOUNT_ID_KEY)
    }
  }
})
